import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import type { DraftMomentPhoto } from "../use-hub-store";

interface SwapCardLayoutProps {
  mentorAvatarUrl?: string;
  mentorName: string;
  menteeAvatarUrl?: string;
  menteeName: string;
  photo: DraftMomentPhoto | null;
  isDragging: boolean;
  isConverting: boolean;
  isUploading: boolean;
  onFileSelect: (files: FileList | null) => void;
  onRemovePhoto: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export function SwapCardLayout({
  mentorAvatarUrl,
  mentorName,
  menteeAvatarUrl,
  menteeName,
  photo,
  isDragging,
  isConverting,
  isUploading,
  onFileSelect,
  onRemovePhoto,
  onDragOver,
  onDragLeave,
  onDrop,
}: SwapCardLayoutProps) {
  return (
    <div className="relative mx-auto h-[450px] w-full max-w-[350px]">
      {/* Back card - Teen's placeholder */}
      <div
        className="absolute inset-0 rounded-2xl border-2 border-dashed border-gray-300 bg-[#f7f5f1]"
        style={{
          transform: "rotate(5deg) translateX(20px) translateY(10px)",
        }}
      >
        <div className="absolute left-3 top-3">
          <UserAvatar
            imageUrl={menteeAvatarUrl}
            name={menteeName}
            size={36}
            showBorder
          />
        </div>
      </div>

      {/* Front card - Mentor's upload */}
      <div
        className={cn(
          "absolute inset-0 overflow-hidden rounded-2xl border-2 bg-[#f7f5f1] transition-colors",
          photo
            ? "border-gray-200"
            : isDragging
              ? "border-dashed border-gray-500"
              : "border-dashed border-gray-300",
        )}
        style={{ transform: "rotate(-4deg)" }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="absolute left-3 top-3 z-10">
          <UserAvatar
            imageUrl={mentorAvatarUrl}
            name={mentorName}
            size={36}
            showBorder
          />
        </div>

        {photo ? (
          <>
            <img
              src={photo.preview}
              alt="Your swap photo"
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={onRemovePhoto}
              disabled={isUploading}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-sm transition-colors hover:bg-white disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </>
        ) : (
          <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2">
            <Upload className="h-6 w-6 text-gray-600" />
            <span className="text-sm text-gray-700">
              {isConverting ? "Processing..." : "Upload photo"}
            </span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif"
              onChange={(e) => onFileSelect(e.target.files)}
              className="hidden"
              disabled={isConverting}
            />
          </label>
        )}
      </div>
    </div>
  );
}
